import React, { useEffect, useState, useRef } from "react";

import { Box, Button, Typography, IconButton, Tooltip } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { toast } from "react-toastify";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import { videosService } from "./services";
import { episodeService } from "../Episode/services";
import { usersService } from "../common/Headerbar/services";
import GenerateCustomShortVideoModal from "./components/GenerateCustomShortVideoModal";
import CreditsWarningModal from "../common/CreditsWarningModal";
import { VideoCard } from "./parts/VideoCard";
import { MainContentLayout } from "./parts/MainContentLayout";
import { API_ERROR_MESSAGE } from "../../constants";
import { RoleName } from "../common/enums/RoleName";
import RoleBasedAccess from "../common/RoleBasedAccess";
import { useCanEdit } from "../../hooks/useCanEdit";
import {
  CustomShortVideoSuggestion,
  CustomVideoModalStep,
  GenerateCustomShortFormVideoIdeasPayload,
} from "./types";
import { trackEvent } from "../../analytics";
import { useWebSocket } from "../../hooks/useWebSocket";
import { WebSocketEventName } from "../common/enums/WebSocketEventName";

export default function Videos() {
  const { id: episodeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const sidebarRef = useRef<SidebarRef>(null);
  const [creditsWarningModalOpen, setCreditsWarningModalOpen] =
    useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [suggestedShortVideos, setSuggestedShortVideos] = useState<
    CustomShortVideoSuggestion[] | null
  >(null);
  const [currentStep, setCurrentStep] = useState<CustomVideoModalStep>("input");
  const canEdit = useCanEdit();
  // fetch videos
  const {
    data,
    refetch,
    isLoading,
    error: videosError,
  } = useQuery({
    queryKey: ["getVideos", episodeId],
    queryFn: () => videosService.getVideos(episodeId),
  });

  const socket = useWebSocket();

  useEffect(() => {
    if (!socket) return undefined;
    const events = [
      WebSocketEventName.SHORT_VIDEO_GENERATED,
      WebSocketEventName.SHORT_VIDEO_GENERATION_FAILED,
      WebSocketEventName.CONNECT,
    ];
    const handler = (payload?: { episode_id?: string }) => {
      if (payload?.episode_id && payload.episode_id !== episodeId) return;
      refetch();
    };
    events.forEach((event) => socket.on(event, handler));
    return () => {
      events.forEach((event) => socket.off(event, handler));
    };
  }, [socket, episodeId, refetch]);

  // fetch episode details
  const { data: episode } = useQuery({
    queryKey: ["getEpisodeById", episodeId],
    queryFn: () => episodeService.getEpisodeById(episodeId),
  });

  // fetch user data for credits
  const { data: userData } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  const availableCredits =
    userData?.organization?.subscription?.available_credits || 0;
  const hasCredits = availableCredits >= 5;

  const generateCustomShortFormVideoIdeasMutation = useMutation({
    mutationKey: ["short-videos-ideas", episodeId],
    mutationFn: (payload: GenerateCustomShortFormVideoIdeasPayload) =>
      videosService.generateCustomShortFormVideoIdeas(
        episodeId || "",
        payload.prompt,
        payload.allowAdditionalCharges,
      ),
    onSuccess: (videoData) => {
      trackEvent("generate_shorts_submitted");
      setSuggestedShortVideos(videoData);
      setCurrentStep("suggested-videos");
    },
    onError: (err) => {
      if (err && typeof err === "object") {
        if ("statusCode" in err) {
          const apiError = err as {
            statusCode?: number;
            message?: string;
            error?: string;
          };
          if (apiError.statusCode === 429) {
            trackEvent("generate_shorts_blocked_credits");
            if (apiError.error === API_ERROR_MESSAGE.INSUFFICIENT_BALANCE) {
              toast.error(`${apiError.error}: ${apiError.message}`);
            } else if (
              apiError.error ===
              API_ERROR_MESSAGE.MAXIMUM_NUMBER_OF_PROMPTS_REACHED
            ) {
              setCreditsWarningModalOpen(true);
            }
          }
        }
      }
    },
  });

  const episodeName = episode?.name || "";

  const totalCount = data?.videos?.length || 0;
  const generatingCount =
    data?.videos?.filter((video) => video.status === "generating" || !video.url)
      ?.length || 0;
  const readyCount =
    data?.videos?.filter((video) => video.url && video.status !== "generating")
      ?.length || 0;

  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const handleRefreshVideos = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await refetch();
      const elapsedTime = Date.now() - startTime;
      const minDelay = 500;
      if (elapsedTime < minDelay) {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), minDelay - elapsedTime);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleConfirmCreditsWarning = () => {
    trackEvent("generate_shorts_overcharge_confirmed");
    generateCustomShortFormVideoIdeasMutation.mutate({
      prompt,
      allowAdditionalCharges: true,
    });
  };

  const renderVideoCards = () => {
    return (
      <VideoCard
        isLoading={isLoading}
        setModalOpen={setModalOpen}
        data={data || { videos: [] }}
        episodeId={episodeId || ""}
      />
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenGenerateModal = params.get("openGenerate") === "true";

    if (shouldOpenGenerateModal) {
      // Viewers cannot generate videos — ignore the auto-open flag for them,
      // but still strip it from the URL below.
      if (canEdit) {
        setModalOpen(true);
      }
      params.delete("openGenerate");
      const nextSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate, canEdit]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <SidebarView pageName={`Episode - ${episodeName}`} ref={sidebarRef} />

      <MainContentLayout
        error={!!videosError}
        isLoading={isLoading}
        renderVideoCards={renderVideoCards}
        episodeName={episodeName}
        episodeId={episodeId || ""}
        handleMobileMenuToggle={handleMobileMenuToggle}
        stats={{
          total: totalCount,
          ready: readyCount,
          generating: generatingCount,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="h6"
            component="h1"
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            Short Videos
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Tooltip title="Refresh videos">
            <span>
              <IconButton
                onClick={handleRefreshVideos}
                disabled={isRefreshing}
                size="small"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "#fff",
                  "&:hover": { backgroundColor: "action.hover" },
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                  "& .MuiSvgIcon-root": isRefreshing
                    ? { animation: "spin 0.8s linear infinite" }
                    : {},
                }}
              >
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
            <Button
              variant="contained"
              onClick={() => {
                trackEvent("generate_shorts_opened");
                setModalOpen(true);
              }}
              size="small"
              startIcon={<AutoAwesomeIcon />}
              sx={{
                backgroundColor: "#2563eb",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 0.875,
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
            >
              Generate custom video
            </Button>
          </RoleBasedAccess>
        </Box>
      </MainContentLayout>

      {modalOpen ? (
        <GenerateCustomShortVideoModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccessfullySaved={() => {
            setModalOpen(false);
            refetch();
          }}
          episode={episode}
          prompt={prompt}
          setPrompt={setPrompt}
          hasCredits={hasCredits}
          setError={setError}
          error={error}
          generateCustomShortFormVideoIdeasMutation={
            generateCustomShortFormVideoIdeasMutation
          }
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          suggestedShortVideos={suggestedShortVideos}
          setSuggestedShortVideos={setSuggestedShortVideos}
        />
      ) : null}

      <CreditsWarningModal
        open={creditsWarningModalOpen}
        onClose={() => setCreditsWarningModalOpen(false)}
        onConfirm={handleConfirmCreditsWarning}
      />
    </Box>
  );
}
